import * as Yup from 'yup';

import { Formik, FormikActions } from 'formik';
import {
  IEditTeamComponentDispatchToProps,
  IEditTeamComponentMapStateToProps,
  IEditTeamFormProps,
  TEditTeam,
} from './EditTeam.d';
import { REQUEST, TARGET_MAX, TARGET_MIN } from '../../consts';
import {
  getCurrencyCode,
  getHeroImage,
  getOccasion,
  getStory,
  getTeamFundraisingTarget,
  getTeamGuid,
  getTeamName,
  getTotalAmount,
} from '../../selectors/apiSelectors';
import {
  getRequestIsError,
  getRequestIsInFlight,
  getRequestIsSuccess,
} from '../../selectors/loadingSelectors';

import EditTeamForm from '../../components/EditTeamForm';
import { Helmet } from 'react-helmet';
import { IAppState } from '../../types/state.d';
import { PageView } from '@justgiving/react-tracking';
import React from 'react';
import { apiThunk as apiThunkAction } from '../../actions';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

export const EditTeam = ({
  isInFlight,
  isError,
  isSuccess,
  teamName,
  currencyCode,
  fundraisingTarget,
  story,
  heroImage,
  totalAmount,
  occasion,
  submitTeamData,
}: TEditTeam) => {
  const { t } = useTranslation();
  const handleSubmit = async (
    data: IEditTeamFormProps,
    { resetForm }: FormikActions<IEditTeamFormProps>,
  ) => {
    try {
      await submitTeamData(data);
      resetForm(data);
    } catch (error) {
      // Errors for this are handled by redux, we just need to block resetForm if editTeam fails
    }
  };
  const validationSchema = Yup.object().shape({
    teamName: Yup.string()
      .max(100, t('content.editteam.name.maxcharacters'))
      .required(t('content.editteam.name.required')),
    fundraisingTarget: Yup.number()
      .min(TARGET_MIN, t('content.editteam.fundraisingtargetmin'))
      .max(TARGET_MAX, t('content.editteam.fundraisingtargetmax')),
  });

  return (
    <PageView
      trackingData={{
        subtype: 'admin',
        event_value: 'edit',
        page_section: 'admin > edit',
      }}
    >
      <Helmet title={t('title.editYourTeam')} />
      <main className="jg-space-ptml jg-space-mbxs jg-space-pbml@md">
        <h1 className="jg-hidden-visually">{t('content.admintabs.edit')}</h1>
        <Formik<IEditTeamFormProps>
          initialValues={{
            teamName,
            fundraisingTarget,
            story,
            heroImage,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          render={(formikProps) => (
            <EditTeamForm
              {...formikProps}
              isInFlight={isInFlight}
              isError={isError}
              isSuccess={isSuccess}
              currencyCode={currencyCode}
              t={t}
              totalAmount={totalAmount}
              occasion={occasion}
            />
          )}
        />
      </main>
    </PageView>
  );
};

export const mapStateToProps = (
  state: IAppState,
): IEditTeamComponentMapStateToProps => ({
  isInFlight: getRequestIsInFlight(state, REQUEST.SUBMIT_TEAM_DATA),
  isError: getRequestIsError(state, REQUEST.SUBMIT_TEAM_DATA),
  isSuccess: getRequestIsSuccess(state, REQUEST.SUBMIT_TEAM_DATA),
  teamGuid: getTeamGuid(state),
  teamName: getTeamName(state),
  currencyCode: getCurrencyCode(state) || 'GBP',
  story: getStory(state),
  heroImage: getHeroImage(state),
  fundraisingTarget: getTeamFundraisingTarget(state),
  totalAmount: getTotalAmount(state),
  occasion: getOccasion(state),
});

export const mapDispatchToProps: IEditTeamComponentDispatchToProps = {
  submitTeamData: apiThunkAction.submitTeamData,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditTeam);
